import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";
import { image1, image2, image3, image4 } from "../customer/imagesStrings";
import { Colors } from "../../../configs";
import {
  ICategorySummary,
  IDayEndStationSummaryResponse,
  IRefundResponse,
  IStoreSummaryResponse,
} from "../../../typings/interfaces/responses/cashierStationResponses";
import ReportHeader from "./ReportHeader";
import ReportFooter from "./ReportFooter";
import { formatUSDate } from "../../../util/common-functions";

export type StationData = {
  station: string;
  openingBalance: number;
  cash: number;
  check: number;
  creditCard: number;
  storeCredit: number;
  monthlyCharge: number;
  discounts: number;
  sales: number;
  registerTotal: number;
  transactionCount: number;
};

export type SalesSummary = {
  base: string;
  ticketsIn: number;
  itemsIn: number;
  ticketsOut: number;
  itemsOut: number;
  incomingSales: number;
  outgoingSales: number;
};

const tableStyles = StyleSheet.create({
  tableContainer: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#A1CEF5",
    borderRadius: "3px",
  },
  tableHeader: {
    backgroundColor: "#A1CEF5",
    flexDirection: "row",
    borderTopLeftRadius: "3px",
    borderTopRightRadius: "3px",
    paddingHorizontal: 12,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 12,
  },
  cell: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    fontSize: 8,
    textAlign: "center",
  },

  col1: { width: "15%" },
  col2: { width: "9.44%" },
  col3: { width: "9.44%" },
  col4: { width: "9.44%" },
  col5: { width: "9.44%" },
  col6: { width: "9.44%" },
  col7: { width: "9.44%" },
  col8: { width: "9.44%" },
  col9: { width: "9.44%" },
  col10: { width: "9.44%" },
  boldText: { fontWeight: "bold" },
});

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    fontFamily: "Helvetica",
    paddingBottom: 60,
  },
  section: {
    marginBottom: 10,
  },
  footer: {
    position: "absolute",
    bottom: 300,
    left: 50,
    width: "100%",
    textAlign: "center",
    fontSize: 40,
    opacity: 0.1,
    transform: "rotate(-45deg)",
  },
});

export const DayEndReport = ({
  month,
  stationData = [],
  salesData = [],
  getStoreSummaryResponse,
  dayEndStationSummaryResponse,
  getStoreCategorySummaryResponse,
  refundsForStore,
}: {
  month: string;
  stationData?: StationData[];
  salesData?: SalesSummary[];
  getStoreSummaryResponse?: IStoreSummaryResponse;
  dayEndStationSummaryResponse?: IDayEndStationSummaryResponse[];
  getStoreCategorySummaryResponse?: ICategorySummary[];
  refundsForStore?: IRefundResponse[];
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <ReportHeader />

      <View
        style={{
          width: "518px",
          backgroundColor: Colors.REPORT_WHITE,
          borderRadius: "15px",
          marginLeft: 48,
          padding: 18,
        }}
        wrap={true}
        break={false}
      >
        <View
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            marginBottom: 30,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              color: Colors.REPORT_GRAY,
              fontWeight: "bold",
            }}
          >
            Daily Report
          </Text>
          <View style={{ textAlign: "right" }}>
            <Text
              style={{
                fontSize: 10,
                color: Colors.REPORT_GRAY,
                fontWeight: "bold",
              }}
            >
              {formatUSDate(new Date())}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: Colors.REPORT_GRAY,
                fontWeight: "bold",
              }}
            >
              00:01 to 23:59
            </Text>
          </View>
        </View>

        <View style={[tableStyles.tableContainer, { marginTop: -25 }]}>
          <View style={[tableStyles.tableHeader, { paddingVertical: 6 }]}>
            <Text style={[tableStyles.cell, tableStyles.col1]}>Station</Text>
            <Text style={[tableStyles.cell, tableStyles.col2]}>
              Opening Balance
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col3]}>Cash</Text>
            <Text style={[tableStyles.cell, tableStyles.col4]}>Check</Text>
            <Text style={[tableStyles.cell, tableStyles.col5]}>
              Credit Card
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col6]}>
              Store Credit
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col7]}>
              Monthly Charge
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col8]}>Discounts</Text>
            <Text style={[tableStyles.cell, tableStyles.col9]}>Sales</Text>
            <Text style={[tableStyles.cell, tableStyles.col10]}>
              Register Total
            </Text>
          </View>

          {dayEndStationSummaryResponse?.map((station, idx) => (
            <View
              key={idx}
              style={[
                tableStyles.tableRow,
                {
                  paddingVertical: 4,
                  paddingTop: 6,
                },
              ]}
            >
              <Text style={[tableStyles.cell, tableStyles.col1]}>
                {station.stationName}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col2]}>
                {station.openingTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col3]}>
                {station.cashPaymentsTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col4]}>
                {station.chequePaymentsTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col5]}>
                {station.cardPaymentsTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col6]}>
                {station.storeCreditPaymentsTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col7]}>
                {station.monthlyPaymentTotal.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col8]}>
                {station.discounts.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col9]}>
                {station.totalSales.toFixed(2)}
              </Text>
              <Text style={[tableStyles.cell, tableStyles.col10]}>
                {station.registerTotal.toFixed(2)}
              </Text>
            </View>
          ))}

          <View
            style={[
              tableStyles.tableRow,
              {
                paddingVertical: 4,
                paddingTop: 6,
              },
            ]}
          >
            <Text style={[tableStyles.cell, tableStyles.col1]}>
              Total No of Transactions
            </Text>

            <Text style={[tableStyles.cell, tableStyles.col3]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.openingTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col4]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.cashPaymentsTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col5]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.chequePaymentsTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col6]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.cardPaymentsTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col7]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.storeCreditPaymentsTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col7]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.monthlyPaymentTotal > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col8]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.discounts > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col9]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.totalSales > 0
                  ).length
                : 0}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col10]}>
              {Array.isArray(dayEndStationSummaryResponse)
                ? dayEndStationSummaryResponse.filter(
                    (item) => item.registerTotal > 0
                  ).length
                : 0}
            </Text>
          </View>

          <View
            style={[
              tableStyles.tableRow,
              {
                paddingVertical: 4,
                backgroundColor: Colors.REPORT_BLUE,
              },
            ]}
          >
            <Text style={[tableStyles.cell, tableStyles.col1]}>Total</Text>
            <Text style={[tableStyles.cell, tableStyles.col2]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.openingTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col3]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.cashPaymentsTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col4]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.chequePaymentsTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col5]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.cardPaymentsTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col6]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.storeCreditPaymentsTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col7]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.monthlyPaymentTotal, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col8]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.discounts, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col9]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.totalSales, 0)
                .toFixed(2)}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.col10]}>
              {dayEndStationSummaryResponse
                ?.reduce((sum, s) => sum + s.registerTotal, 0)
                .toFixed(2)}
            </Text>
          </View>
        </View>

        <View style={[tableStyles.tableContainer, { marginTop: 20 }]}>
          <View
            style={{
              flexDirection: "row",
              backgroundColor: Colors.REPORT_BLUE,
            }}
          >
            {[
              "Base",
              "Tickets In",
              "Items In",
              "Tickets Out",
              "Items Out",
              "Incoming Sales",
              "Outgoing Sales",
            ].map((label, idx) => (
              <Text
                key={idx}
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 6,
                  textAlign: "center",
                }}
              >
                {label}
              </Text>
            ))}
          </View>

          {getStoreCategorySummaryResponse?.map((entry, idx) => (
            <View
              key={idx}
              style={{
                flexDirection: "row",
                height: 20,
              }}
            >
              <Text style={{ flex: 1, fontSize: 8, padding: 4 }}>
                {entry.categoryName}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {entry.billsInCount}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {entry.itemsInCount}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {entry.billsOutCount}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {entry.itemsOutCount}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {0.0}
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                {0.0}
              </Text>
            </View>
          ))}

          <View
            style={{
              flexDirection: "row",
              backgroundColor: Colors.REPORT_BLUE,
              height: 20,
            }}
          >
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              Total
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {getStoreCategorySummaryResponse?.reduce(
                (sum, s) => sum + s.billsInCount,
                0
              )}
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {getStoreCategorySummaryResponse?.reduce(
                (sum, s) => sum + s.itemsInCount,
                0
              )}
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {getStoreCategorySummaryResponse?.reduce(
                (sum, s) => sum + s.billsOutCount,
                0
              )}
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {getStoreCategorySummaryResponse?.reduce(
                (sum, s) => sum + s.itemsOutCount,
                0
              )}
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {salesData
                .reduce((sum, s) => sum + s.incomingSales, 0)
                .toFixed(2)}
            </Text>
            <Text
              style={{ flex: 1, fontSize: 8, padding: 3, textAlign: "center" }}
            >
              {salesData
                .reduce((sum, s) => sum + s.outgoingSales, 0)
                .toFixed(2)}
            </Text>
          </View>
        </View>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 20,
            gap: 10,
          }}
        >
          <View
            style={{
              width: 730,
              borderWidth: 1,
              borderColor: "#A1CEF5",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header row 1 */}
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "#A1CEF5",
                height: 20,
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  width: 135,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                  borderRightWidth: 0.5,
                  borderRightColor: Colors.WHITE,
                }}
              >
                Station
              </Text>
              <Text
                style={{
                  width: 598,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                  borderBottomWidth: 0.5,
                  borderBottomColor: Colors.WHITE,
                }}
              >
                Refunds ($)
              </Text>
            </View>

            <View
              style={{
                flexDirection: "row",
                backgroundColor: "#A1CEF5",
                height: 20,
              }}
            >
              <Text style={{ width: 132 }} />
              {["Cash", "Credit Cards", "Check", "Store Credit"].map(
                (label, idx) => (
                  <Text
                    key={idx}
                    style={{
                      width: 145,
                      fontSize: 8,
                      textAlign: "center",
                      padding: 4,
                      borderLeftWidth: idx === 0 ? 0.5 : 0,
                      borderLeftColor: Colors.WHITE,
                    }}
                  >
                    {label}
                  </Text>
                )
              )}
            </View>
            {refundsForStore?.map((item, idx) => (
              <View key={idx} style={{ flexDirection: "row", height: 20 }}>
                <Text
                  style={{
                    width: 135,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {item.stationAccessId}
                </Text>
                <Text
                  style={{
                    width: 145,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {item.cashRefundTotal.toFixed(2)}
                </Text>
                <Text
                  style={{
                    width: 145,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {item.cardRefundTotal.toFixed(2)}
                </Text>
                <Text
                  style={{
                    width: 145,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {0.0}
                </Text>
                <Text
                  style={{
                    width: 145,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {item.storeCreditRefundTotal.toFixed(2)}
                </Text>
              </View>
            ))}

            <View
              style={{
                flexDirection: "row",
                backgroundColor: "#A1CEF5",
                height: 30,
                paddingRight: 6,
                paddingLeft: 10,
              }}
            >
              <Text
                style={{
                  width: 135,
                  fontSize: 7,
                  paddingVertical: 4,
                  paddingLeft: 4,
                  textAlign: "left",
                  lineHeight: 1.2,
                  paddingTop: 8,
                }}
              >
                Total No of{"\n"}
                Transactions
              </Text>

              <Text
                style={{
                  width: 50,
                  fontSize: 8,
                  textAlign: "right",
                  paddingVertical: 4,
                  paddingTop: 8,
                }}
              >
                {refundsForStore
                  ?.reduce((sum, r) => sum + (r.cashRefundTotal || 0), 0)
                  .toFixed(2)}
              </Text>

              <Text
                style={{
                  width: 140,
                  fontSize: 8,
                  padding: 4,
                  paddingTop: 8,
                  textAlign: "right",
                }}
              >
                {refundsForStore
                  ?.reduce((sum, r) => sum + (r.cardRefundTotal || 0), 0)
                  .toFixed(2)}
              </Text>

              <Text
                style={{
                  width: 145,
                  fontSize: 8,
                  padding: 4,
                  paddingTop: 8,
                  paddingRight: 16,
                  textAlign: "right",
                }}
              >
                {0.0}
              </Text>

              <Text
                style={{
                  width: 145,
                  fontSize: 8,
                  padding: 4,
                  paddingRight: 14,
                  textAlign: "right",
                  paddingTop: 8,
                }}
              >
                {refundsForStore
                  ?.reduce((sum, r) => sum + (r.storeCreditRefundTotal || 0), 0)
                  .toFixed(2)}
              </Text>
            </View>
          </View>

          <View
            style={{
              width: 380,
              borderWidth: 1,
              borderColor: "#A1CEF5",
              borderRadius: 3,
              marginLeft: 10,
              overflow: "hidden",
            }}
          >
            <View style={{ backgroundColor: "#A1CEF5", height: 20 }}>
              <Text style={{ fontSize: 8, padding: 4, textAlign: "center" }}>
                Daily Summary
              </Text>
            </View>

            <View style={{ height: 0.5, backgroundColor: Colors.WHITE }} />
            <View
              style={{
                backgroundColor: "#A1CEF5",
                flexDirection: "row",
                height: 19.5,
              }}
            >
              <Text
                style={{
                  flex: 2,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                Totals
              </Text>
              <Text
                style={{
                  flex: 1,
                  fontSize: 8,
                  padding: 4,
                  textAlign: "center",
                }}
              >
                Amount
              </Text>
            </View>

            {[
              {
                label: "No. of Customers",
                value: getStoreSummaryResponse?.totalCustomers,
              },
              {
                label: "No. of New Customers",
                value: getStoreSummaryResponse?.newCustomers,
              },
              {
                label: "No. of Pickups",
                value: getStoreSummaryResponse?.pickups,
              },
              {
                label: "No. of Drop Offs",
                value: getStoreSummaryResponse?.dropOffs,
              },
            ].map((item, idx) => (
              <View key={idx} style={{ flexDirection: "row", height: 27.2 }}>
                <Text style={{ flex: 2, fontSize: 8, padding: 4 }}>
                  {item.label}
                </Text>
                <Text
                  style={{
                    flex: 1,
                    fontSize: 8,
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  {item.value}
                </Text>
              </View>
            ))}

            <View style={{ height: 21, backgroundColor: "#A1CEF5" }} />
          </View>
        </View>
      </View>

      <ReportFooter />
    </Page>
  </Document>
);

export const DayEndReportDownload = () => (
  <PDFDownloadLink
    document={<DayEndReport month="February - 2025" stationData={[]} />}
    fileName="Day_End_Report_Feb_2025.pdf"
  >
    {({ loading }) =>
      loading ? (
        <span>Preparing Day End Report...</span>
      ) : (
        <span>Download Day End Report</span>
      )
    }
  </PDFDownloadLink>
);
